<template>
  <v-container
  v-if="isDataReady"
  fluid>
    <add-repair-part-dialog
    v-if="addRepairPartDialog.model"
    v-model="addRepairPartDialog.model"
    :repair-id="Number($route.params.id)"
    @add="addNewRepairPart"/>
    <request-id-chip
    v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN'])"
    :id="repairProp.number"
    justify="end"/>
    <v-row 
    dense 
    no-gutters 
    class="mt-2 mb-4">
        <v-col>
          <p v-if="$parent.isRole(['SUPERVISOR'])" class="process-repair-tab-section-subtitle text-center">
           {{$t('pages.repair.partsAreInService')}}
          </p>
          <div
          v-else-if="$parent.isRole(['CLIENT'])">
            <p class="process-repair-tab-section-subtitle text-center">
            {{$t('pages.repair.partsAreInDiagnostic')}}
            </p>
            <p
            v-if="withPredefinedTypes"
            class="text-h6 text-end mr-10">
              {{$t('headers.totalTypes')}}: <span>{{getTotalTypesOfAll}}</span>
            </p>
            <p
            v-else
            class="text-h6 text-end mr-10">
              {{$t('headers.totalPrice')}}: <span>{{getTotalPriceOfAll}}</span>
            </p>
          </div>
        </v-col>
    </v-row>
    <section-title-with-request-id
    v-if="$parent.isRole(['SUPERVISOR', 'SERVICE', 'CLIENT', 'SUSTAINABILITY_MANAGER'])"
    :request-id="repairProp.number"
    title="pages.repair.partsInfo"/>
    <v-form
    ref="form"
    v-model="isFormValid">
      <v-row 
      dense>
        <v-col>
          <attachment-dialog
          v-if="attachmentDialog.model"
          v-model="attachmentDialog.model"
          :attachments.sync="attachmentDialog.attachments"
          preview/>
          <main-data-table
          v-if="isDataReady"
          :headers="getHeaders"
          :items="newData.repairParts"
          number-pages
          set-image
          :with-search="false"
          :page.sync="page"
          :size="-1"
          :items-per-page-options="[-1]"
          :total-elements="-1"
          hide-default-footer>
            <template v-slot:[`item.serialNumber`]="{ item }">
                <text-field-value 
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.serialNumber" 
                normal/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR'])">
                  {{item.serialNumber}}
                </span>
            </template>
            <template v-slot:[`item.serviceTag`]="{ item }">
                <text-field-value 
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.serviceTag" 
                normal/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR'])">
                  {{item.serviceTag}}
                </span>
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <text-field-autocomplete 
                :label="$t('textFields.selectStatus')"
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.statusObj"
                :items="serviceStatuses"
                normal 
                itemText="value"
                style="max-width: 14rem"
                :clearable="false"/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER', 'CLIENT'])">
                  {{item.diagnosticInfo.statusObj ? $t(item.diagnosticInfo.statusObj.value) : ''}}
                </span>
            </template>
            <template v-slot:[`item.comment`]="{ item }">
                <div class="py-2">
                  <text-area
                  v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                  v-model="item.diagnosticInfo.comment"
                  solo-normal
                  width="14rem"
                  no-resize/> 
                  <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER', 'CLIENT'])">
                    {{item.diagnosticInfo.comment}}
                  </span>
                </div>
            </template>
            <template v-slot:[`item.attachments`]="{ item }">
              <attachments-dialog-trigger
              @click="openAttachmentsDialog(item.attachments)"
              :attachments="item.attachments"
              disable-on-empty/>
            </template>
            <template v-slot:[`item.diagnosticPrice`]="{ item }">
                <text-field-number
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.pricesOrPredefinedTypes.diagnosticPrice"
                @input="calcTotalPriceOfItem(item.diagnosticInfo)"
                normal
                double/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.pricesOrPredefinedTypes.diagnosticPrice}}
                </span>
            </template>
            <template v-slot:[`item.diagnosticTypes`]="{ item }">
                <predefined-services
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypes"
                @changed="(tps) => getTotalOfTypesForItem(item, tps, 'diagnosticTypesText')"
                :items="item.diagnosticInfo.availablePredefinedTypesForDiagnostic"
                no-avaiable-items-label="textFields.noAvailableDiagnosticTypesForPart"
                :max="1"/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypesText}}
                </span>
            </template>
            <template v-slot:[`item.repairPrice`]="{ item }">
                <text-field-number
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.pricesOrPredefinedTypes.repairPrice"
                @input="calcTotalPriceOfItem(item.diagnosticInfo)"
                normal
                double/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.pricesOrPredefinedTypes.repairPrice}}
                </span>
            </template>
            <template v-slot:[`item.repairTypes`]="{ item }">
                <predefined-services
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.pricesOrPredefinedTypes.repairTypes"
                @changed="(tps) => getTotalOfTypesForItem(item, tps, 'repairTypesText')"
                :items="item.diagnosticInfo.availablePredefinedTypesForRepair"
                no-avaiable-items-label="textFields.noAvailableRepairTypesForPart"/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.pricesOrPredefinedTypes.repairTypesText}}
                </span>
            </template>
            <template v-slot:[`item.partsPrice`]="{ item }">
                <text-field-number
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.pricesOrPredefinedTypes.partsPrice"
                @input="calcTotalPriceOfItem(item.diagnosticInfo)"
                normal
                double/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.pricesOrPredefinedTypes.partsPrice}}
                </span>
            </template>
            <template v-slot:[`item.partsTypes`]="{ item }">
                <predefined-services
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.pricesOrPredefinedTypes.partsTypes"
                @changed="(tps) => getTotalOfTypesForItem(item, tps, 'partsTypesText')"
                :items="item.diagnosticInfo.availablePredefinedTypesForParts"
                no-avaiable-items-label="textFields.noAvailablePartsTypesForPart"
                no-avaiable-items-label-color="black"/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.pricesOrPredefinedTypes.partsTypesText}}
                </span>
            </template>
            <template v-slot:[`item.totalCost`]="{ item }">
                <text-field-number
                v-if="!disabled && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.diagnosticInfo.totalCost"
                normal
                readonly
                double/>
                <span v-else-if="disabled || $parent.isRole(['SUPERVISOR', 'SUSTAINABILITY_MANAGER'])">
                  {{item.diagnosticInfo.totalCost}}
                </span>
            </template>
            <template 
            v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
            v-slot:[`item.action`]="{ item }">
                <div 
                class="d-flex justify-start">
                  <primary-btn
                  @click="tryToSaveRow(item)"
                  :loading="isLoading"
                  :label="$t('btns.save')"
                  color="#4285f4"
                  class="mx-2"
                  :disabled="disabled"
                  small-text
                  :width="50"
                  :height="30"/>
                </div>
            </template>
          </main-data-table>
          <v-row
          v-if="canAddRepairPart"
          dense
          class="mt-5">
            <v-col
            class="d-flex justify-center">
              <outlined-btn
              @click="openAddRepairPartDialog"
              :label="$t('btns.addRepairPart')"
              :disabled="disabled"
              icon="plus-circle"/>
            </v-col>
          </v-row>
          <v-row
          v-if="$parent.isNotRole(['CLIENT'])"
          dense
          class="mt-5">
            <v-col
            v-if="withPredefinedTypes">
              <div class="d-flex justify-end">
                <total-types-single-row-table :parts="newData.repairParts"/>
              </div>
            </v-col>
            <v-col
            v-else
            class="mr-13">
              <p
              class="text-end">
                {{$t('headers.diagnosticPriceTotal')}}: <strong>{{getTotalOfPrices('diagnosticPrice')}}</strong>
              </p>
              <p
              class="text-end">
                {{$t('headers.repairPriceTotal')}}: <strong>{{getTotalOfPrices('repairPrice')}}</strong>
              </p>
              <p
              class="text-end">
                {{$t('headers.partsPriceTotal')}}: <strong>{{getTotalOfPrices('partsPrice')}}</strong>
              </p>
              <p
              class="text-h6 text-end">
                {{$t('headers.totalPrice')}}: <span>{{getTotalPriceOfAll}}</span>
              </p>
            </v-col>
          </v-row>
          <v-row 
          v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
          dense
          class="mt-5">
            <v-col>
              <process-repair-quick-btns 
              :loading.sync="isLoading"
              :config="getQuickBtnsConfig"
              on-success-repair-status-message="dialogs.messages.success.proceedingToNextStepSuccessfully"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
    <export-xlsx-row
    :width="150"
    :height="50"
    :disabled="newData.repairParts.length === 0"
    :loading.sync="isLoading"
    class="px-4 mt-4"
    name="export.title.repairDiagnosticsTab"
    :exporter="getExporter"/>
  </v-container>
</template>
<script>
import TextArea from '@/components/text-fields/TextArea'
import TextFieldValue from '@/components/text-fields/TextFieldValue'
import TextFieldAutocomplete from '@/components/text-fields/TextFieldAutocomplete'
import TextFieldNumber from '@/components/text-fields/TextFieldNumber'
import ProcessRepairQuickBtns from '@/components/process-repair-tabs/ProcessRepairQuickBtns'
import ExportXlsxRow from '@/components/common/ExportXlsxRow'
import MainDataTable from '@/components/common/MainDataTable'
import AttachmentDialog from '@/components/dialogs/AttachmentDialog'
import AttachmentsDialogTrigger from '@/components/common/triggers/AttachmentsDialogTrigger'
import PredefinedServices from '@/components/common/process-tabs/shared/PredefinedServices'
import RequestIdChip from '@/components/common/process-tabs/shared/RequestIdChip'
import SectionTitleWithRequestId from '@/components/common/process-tabs/shared/SectionTitleWithRequestId'
import TotalTypesSingleRowTable from '@/components/common/basic-tables/TotalTypesSingleRowTable'
import AddRepairPartDialog from '@/components/dialogs/AddRepairPartDialog'
import OutlinedBtn from '@/components/btns/OutlinedBtn'
import PrimaryBtn from '@/components/btns/PrimaryBtn'

import { getTotalOfTypes } from "@/services/partsPreviewService"
import { getUserRole } from '@/services/authService'
import { updateDiagnosticData } from '@/repositories/repairRepo'
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// getDiagnosticStatusByKey
import { DIAGNOSTICS_STATUSES, getObjectDiagnosticStatusByKey } from '@/utils/statusesUtil'
import { execHttpRequestThrowable } from '@/services/http'
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// compareArrays
import { copyObj, cloneObj } from '@/utils/StringUtil'
import { exportRepairDiagnosticsTabXlsx } from '@/repositories/exportRepo'
export default {
  components: {
    ProcessRepairQuickBtns,
    ExportXlsxRow,
    TextArea,
    TextFieldValue,
    TextFieldAutocomplete,
    TextFieldNumber,
    MainDataTable,
    AttachmentDialog,
    AttachmentsDialogTrigger,
    PredefinedServices,
    RequestIdChip,
    SectionTitleWithRequestId,
    TotalTypesSingleRowTable,
    AddRepairPartDialog,
    OutlinedBtn,
    PrimaryBtn
  },
  data() {
    return {
      isFormValid: false,
      headers: [{
          text: 'headers.partName',
          align: 'start',
          sortable: true,
          value: 'partName',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.serialNumber',
          align: 'start',
          sortable: true,
          value: 'serialNumber',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.serviceTag',
          align: 'start',
          sortable: true,
          value: 'serviceTag',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.status',
          align: 'start',
          sortable: true,
          value: 'status',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.comment',
          align: 'start',
          sortable: true,
          value: 'comment',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.attachments',
          align: 'start',
          sortable: true,
          value: 'attachments',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        },
         // NOTE: these two columns cannot exist in the same time
        {
          text: 'headers.diagnosticPrice',
          align: 'start',
          sortable: true,
          value: 'diagnosticPrice',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.diagnosticTypes',
          align: 'start',
          sortable: true,
          value: 'diagnosticTypes',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, 
        // NOTE: these two columns cannot exist in the same time
        {
          text: 'headers.repairPrice',
          align: 'start',
          sortable: true,
          value: 'repairPrice',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.repairTypes',
          align: 'start',
          sortable: true,
          value: 'repairTypes',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, 
        // NOTE: these two columns cannot exist in the same time
        {
          text: 'headers.partsPrice',
          align: 'start',
          sortable: true,
          value: 'partsPrice',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.partsUsed',
          align: 'start',
          sortable: true,
          value: 'partsTypes',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.totalCost',
          align: 'start',
          sortable: true,
          value: 'totalCost',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.action',
          align: 'start',
          sortable: false,
          value: 'action',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SERVICE"]
        }
      ],
      serviceStatuses: DIAGNOSTICS_STATUSES,
      page: 1,
      isLoading: false,
      newData: null,
      isDataReady: false,
      attachmentDialog: {
        model: false
      },
      withPredefinedTypes: false,
      addRepairPartDialog: {
        model: false
      }
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    disabled: Boolean
  },
  computed: {
    canAddRepairPart() {
      return this.$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE']) && this.$parent.isRepairStatus(['IN_DIAGNOSTICS']) && !this.disabled
    },
    getRole() {
      return getUserRole()
    },
    repairProp: {
      get: function() {
        return this.$props.data
      },
      set: function(v) {
        this.$emit('update:data', v)
      }
    },
    getTotal() {
      if (this.getTotalOfPrices)
        return this.getTotalOfPrices('diagnosticPrice') + 
        this.getTotalOfPrices('repairPrice') + 
        this.getTotalOfPrices('partsPrice')
      return 0
    },
    getTotalPriceOfAll() {
      let t = 0
      for (const di of this.newData.repairParts.map(rp => rp.diagnosticInfo)) {
        t += this.calcTotalPriceOfItem(di)
      }

      return t
    },
    getTotalTypesOfAll() {
      const NONE = this.$t("headers.none")

      const texts = []
      const dTypes = getTotalOfTypes('diagnosticTypes', this.newData.repairParts, c => this.$t(c))
      if (dTypes !== NONE) texts.push(dTypes.trim())
      const rTypes = getTotalOfTypes('repairTypes', this.newData.repairParts, c => this.$t(c))
      if (rTypes !== NONE) texts.push(rTypes.trim())
      const pTypes = getTotalOfTypes('partsTypes', this.newData.repairParts, c => this.$t(c))
      if (pTypes !== NONE) texts.push(pTypes.trim())

      let build = ""
      for(let i = 0; i < texts.length; i++) {
        build = build.concat(texts[i])
        if (i + 1 < texts.length) build = build.concat(" + ")
      }

      return build.length > 0 ? build : NONE
    },
    getQuickBtnsConfig() {
      return [
        {
          name: 'Save',
          events: {
            'click': this.setChangedDialogItems
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'SubmitForApproval',
          props: {
            'disabled': this.disabled || !this.newData.isAllowedToContinue
          },
          events: 'AsContinue'
        }
      ]
    },
    getExporter() {
      return async () => await exportRepairDiagnosticsTabXlsx(this.$route.params.id)
    },
    getHeaders() {
      const headers = []
      const hideHeadersForRequestWithPredefinedTypes = ["totalCost", "partsPrice", "repairPrice", "diagnosticPrice"]
      const hideHeadersForRequestWithPrices = ["partsTypes", "repairTypes", "diagnosticTypes"]

      for (const h of this.headers) {
        if (!h.permissions.includes(this.getRole)) continue
        if (this.withPredefinedTypes) {
          if (hideHeadersForRequestWithPredefinedTypes.includes(h.value)) continue
        }
        else {
          if (hideHeadersForRequestWithPrices.includes(h.value)) continue
        }
        headers.push(h)
      }
      return headers
    }
  },
  methods: {
    async tryToSaveRow(part) {
      const UPDATED_PART = (await this.tryToSaveData([part]))[0]
      
      const OLD_PART = this.repairProp.repairParts.filter(p => p.id === part.id)[0]
      cloneObj(UPDATED_PART, OLD_PART)
    },
    async tryToSaveAll() {
      const UPDATED_PARTS = await this.tryToSaveData(this.newData.repairParts)

      const OLD_PARTS = this.repairProp.repairParts.filter(p => p.initialApproval === 'APPROVED')
      for (let i = 0; i < OLD_PARTS.length; i++) cloneObj(UPDATED_PARTS[i], OLD_PARTS[i])
    },
    async tryToSaveData(partsToSave) {
      const messages = {
        _200: 'dialogs.messages.success.dataSavedSuccessfully'
      }
      const PARTS_READY = partsToSave
      PARTS_READY.forEach(p => {
        p.diagnosticInfo.status = p.diagnosticInfo.statusObj ? String(p.diagnosticInfo.statusObj.key) : null
      })
      const DATA = PARTS_READY.map(p => {
        return {
          id: p.id,
          serialNumber: p.serialNumber,
          serviceTag: p.serviceTag,
          diagnosticInfo: {
            status: p.diagnosticInfo.status,
            totalCost: p.diagnosticInfo.totalCost,
            comment: p.diagnosticInfo.comment,
            pricesOrPredefinedTypes: p.diagnosticInfo.pricesOrPredefinedTypes,
            withPredefinedTypes: p.diagnosticInfo.withPredefinedTypes
          }
        }
      })
      const r = await execHttpRequestThrowable(async () => await updateDiagnosticData(this.$route.params.id, DATA),
      messages,
      v => this.isLoading = v)
      this.newData.isAllowedToContinue = r.response.data

      return PARTS_READY
    },
    setChangedDialogItems() {
      this.tryToSaveAll()  
      // NOTE: ENABLE_CHANGE_DIALOG - Enable this code, if changes dialog is needed.
      //-----------------------------------------------------------------
      // const oldParts = this.repairProp.repairParts.filter(p => p.initialApproval === 'APPROVED')
      // const newParts = this.newData.repairParts
      // const props = [
      //   {
      //     field: 'headers.serialNumber',
      //     prop: 'serialNumber'
      //   }, {
      //     field: 'headers.serviceTag',
      //     prop: 'serviceTag'
      //   }, {
      //     field: 'headers.status',
      //     prop: 'diagnosticInfo.statusObj.key',
      //     getter: getDiagnosticStatusByKey
      //   }, {
      //     field: 'headers.diagnosticPrice',
      //     prop: 'diagnosticInfo.pricesOrPredefinedTypes.diagnosticPrice'
      //   },  {
      //     field: 'headers.repairPrice',
      //     prop: 'diagnosticInfo.pricesOrPredefinedTypes.repairPrice'
      //   }, {
      //     field: 'headers.partsPrice',
      //     prop: 'diagnosticInfo.pricesOrPredefinedTypes.partsPrice'
      //   }, {
      //     field: 'headers.diagnosticTypes',
      //     prop: 'diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypesText'
      //   }, {
      //     field: 'headers.repairTypes',
      //     prop: 'diagnosticInfo.pricesOrPredefinedTypes.repairTypesText'
      //   }, {
      //     field: 'headers.partsUsed',
      //     prop: 'diagnosticInfo.pricesOrPredefinedTypes.partsTypesText'
      //   }, {
      //     field: 'headers.totalCost',
      //     prop: 'diagnosticInfo.totalCost'
      //   }, {
      //     field: 'headers.comment',
      //     prop: 'diagnosticInfo.comment'
      //   }
      // ]
      
      // this.$changesDialog.open(
      //   'dialogs.titles.changes',
      //   compareArrays(oldParts, newParts, props, true),
      //   this.tryToSaveAll,
      //   true
      // )
      //-----------------------------------------------------------------
    },
    openAttachmentsDialog(attachments) {
      this.attachmentDialog = {
        model: true,
        attachments
      }
    },
    openAddRepairPartDialog() {
      this.addRepairPartDialog.model = true
    },
    getTotalOfPrices(prop) {
      let total = 0.0
      const isTotalCost = prop === 'totalCost'
      this.newData.repairParts.forEach(rp => {
        if (rp.diagnosticInfo) {
          const v = isTotalCost ? rp.diagnosticInfo.totalCost : rp.diagnosticInfo.pricesOrPredefinedTypes[prop]
          if (v !== null && v !== '') {
            total += Number.parseFloat(v)
          }
        }
      })

      return total
    },
    getTotalOfTypesForItem(item, types, prop) {
      let total = ""
      let dic = {}
      for (const t of types) {
        if (!(t.value in dic)) {
          dic[t.value] = {
            text: t.shortText,
            number: 1
          }
        }
        else {
          let n = Number(dic[t.value].number)
          dic[t.value].number = ++n
        }
      }

      const typeKeys = Object.keys(dic)
      for(let i = 0; i < typeKeys.length; i++) {
        const key = typeKeys[i]
        total = total.concat(`${dic[key].number} x ${dic[key].text}`)
        if (i + 1 < typeKeys.length) total = total.concat(" + ")
      }

      item.diagnosticInfo.pricesOrPredefinedTypes[prop] = total.length > 0 ? total : null
    },
    calcTotalPriceOfItem(di) {
      const prices = di.pricesOrPredefinedTypes

      const pp = Number.parseFloat(prices.partsPrice)
      const rp = Number.parseFloat(prices.repairPrice)
      const dp = Number.parseFloat(prices.diagnosticPrice)

      di.totalCost = (Number.isNaN(pp) ? 0 : pp) + (Number.isNaN(rp) ? 0 : rp) + (Number.isNaN(dp) ? 0 : dp)

      return di.totalCost
    },
    addNewRepairPart(part) {
      part.diagnosticInfo.statusObj = getObjectDiagnosticStatusByKey(part.diagnosticInfo.status)
      part.diagnosticInfo.totalCost = part.diagnosticInfo.totalCost ? Number.parseFloat(part.diagnosticInfo.totalCost) : 0
      if (this.withPredefinedTypes) {
        this.getTotalOfTypesForItem(part, part.diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypes, "diagnosticTypesText")
        this.getTotalOfTypesForItem(part, part.diagnosticInfo.pricesOrPredefinedTypes.repairTypes, "repairTypesText")
        this.getTotalOfTypesForItem(part, part.diagnosticInfo.pricesOrPredefinedTypes.partsTypes, "partsTypesText")
      }

      this.repairProp.repairParts.push(part)
      this.newData.repairParts.push(copyObj(part))
    }
  },
  mounted() {
    // NOTE: if ever a part has to be changed (its prop or something) do it in method /addNewRepairPart/ too.
    this.withPredefinedTypes = this.repairProp.repairParts[0].diagnosticInfo.withPredefinedTypes
    this.newData = {
      repairParts: [],
      isAllowedToContinue: this.repairProp.isAllowedToContinue
    }
    this.repairProp.repairParts.filter(rp => rp.initialApproval === 'APPROVED').forEach(p => {
      p.diagnosticInfo.statusObj = getObjectDiagnosticStatusByKey(p.diagnosticInfo.status)
      p.diagnosticInfo.totalCost = p.diagnosticInfo.totalCost ? Number.parseFloat(p.diagnosticInfo.totalCost) : 0
      if (this.withPredefinedTypes) {
        this.getTotalOfTypesForItem(p, p.diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypes, "diagnosticTypesText")
        this.getTotalOfTypesForItem(p, p.diagnosticInfo.pricesOrPredefinedTypes.repairTypes, "repairTypesText")
        this.getTotalOfTypesForItem(p, p.diagnosticInfo.pricesOrPredefinedTypes.partsTypes, "partsTypesText")
      }
      this.newData.repairParts.push(copyObj(p))
    })
    this.isDataReady = true
  }
}
</script>
<style>

</style>